// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Users, DollarSign, AlertTriangle, Target, UserCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  trend?: "up" | "down" | "neutral";
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon: Icon, trend = "neutral" }) => {
  const trendColor = {
    up: "text-green-600",
    down: "text-red-600",
    neutral: "text-gray-600",
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {change && (
              <p className={`text-sm ${trendColor[trend]} flex items-center gap-1`}>
                <TrendingUp className="h-3 w-3" />
                {change}
              </p>
            )}
          </div>
          <div className="p-3 bg-primary-50 rounded-lg">
            <Icon className="h-6 w-6 text-primary-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const CoachAnalytics: React.FC = () => {
  const [metrics, setMetrics] = useState({
    newSignups: 0,
    totalProspects: 0,
    conversionRate: 0,
    monthlyRevenue: 0,
    churnRate: 0,
    activeSubscribers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      // Fetch coach prospects
      const { data: prospects, error: prospectsError } = await supabase
        .from("coach_prospects")
        .select("*");

      if (prospectsError) throw prospectsError;

      // Fetch coach subscriptions
      const { data: subscriptions, error: subscriptionsError } = await supabase
        .from("coach_subscriptions")
        .select("*");

      if (subscriptionsError) throw subscriptionsError;

      // Calculate metrics
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const newSignupsThisMonth = prospects?.filter(p => {
        const createdDate = new Date(p.created_at);
        return createdDate.getMonth() === currentMonth && createdDate.getFullYear() === currentYear;
      }).length || 0;

      const activeSubscribers = subscriptions?.filter(s => 
        s.payment_status === 'active' || s.payment_status === 'paid'
      ).length || 0;

      const totalRevenue = subscriptions?.reduce((sum, sub) => 
        sum + (Number(sub.monthly_fee) || 0), 0
      ) || 0;

      const paidProspects = prospects?.filter(p => 
        p.pipeline_stage === 'paid_subscriber' || p.pipeline_stage === 'champion'
      ).length || 0;

      const conversionRate = prospects?.length ? (paidProspects / prospects.length) * 100 : 0;

      const churnedCount = subscriptions?.filter(s => 
        s.payment_status === 'cancelled' || s.churn_risk_score && s.churn_risk_score > 70
      ).length || 0;

      const churnRate = subscriptions?.length ? (churnedCount / subscriptions.length) * 100 : 0;

      setMetrics({
        newSignups: newSignupsThisMonth,
        totalProspects: prospects?.length || 0,
        conversionRate: Math.round(conversionRate * 100) / 100,
        monthlyRevenue: Math.round(totalRevenue),
        churnRate: Math.round(churnRate * 100) / 100,
        activeSubscribers,
      });
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
      <MetricCard
        title="New Signups (This Month)"
        value={metrics.newSignups}
        change="+12% from last month"
        icon={UserCheck}
        trend="up"
      />
      <MetricCard
        title="Total Prospects"
        value={metrics.totalProspects}
        icon={Users}
      />
      <MetricCard
        title="Conversion to Paid"
        value={`${metrics.conversionRate}%`}
        change="+2.3% from last month"
        icon={Target}
        trend="up"
      />
      <MetricCard
        title="Monthly Recurring Revenue"
        value={`$${metrics.monthlyRevenue.toLocaleString()}`}
        change="+18% from last month"
        icon={DollarSign}
        trend="up"
      />
      <MetricCard
        title="Churn Rate"
        value={`${metrics.churnRate}%`}
        change="-1.2% from last month"
        icon={AlertTriangle}
        trend="up"
      />
      <MetricCard
        title="Active Subscribers"
        value={metrics.activeSubscribers}
        change="+15 this month"
        icon={Users}
        trend="up"
      />
    </div>
  );
};